import { useState, useEffect, useRef } from "react";

/* ══════════════════════════════════════════════
   CONSTANTS
   ══════════════════════════════════════════════ */

export const STEP_LABELS = ["Basics", "Hours", "Roles & Staffing", "Employees", "Generate"];
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const FULL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const PERIODS = ["Breakfast", "Lunch", "Dinner"];

export const PERIOD_ICONS = { Breakfast: "\u2600\uFE0F", Lunch: "\uD83C\uDF24\uFE0F", Dinner: "\uD83C\uDF19", "Late Night": "\uD83C\uDF03" };
export const PERIOD_LABELS = { Breakfast: "Morning", Lunch: "Midday", Dinner: "Evening", "Late Night": "Night" };
export const PERIOD_TIMES_DISPLAY = { Breakfast: "6 AM \u2013 11 AM", Lunch: "11 AM \u2013 4 PM", Dinner: "4 PM \u2013 Close" };

export const DEFAULT_PERIODS = {
  Breakfast: { active: false, start: "06:00", end: "11:00" },
  Lunch: { active: true, start: "11:00", end: "16:00" },
  Dinner: { active: true, start: "16:00", end: "22:00" },
  "Late Night": { active: false, start: "22:00", end: "02:00" },
};

export const ROLE_PRESETS = [
  { name: "Cook", type: "BOH", color: "#E85D3A" },
  { name: "Prep Cook", type: "BOH", color: "#D4742C" },
  { name: "Dishwasher", type: "BOH", color: "#8B6914" },
  { name: "Server", type: "FOH", color: "#2D8F5E" },
  { name: "Bartender", type: "FOH", color: "#6C5CE7" },
  { name: "Host", type: "FOH", color: "#0891B2" },
  { name: "Busser", type: "FOH", color: "#7C6B5A" },
  { name: "Manager", type: "FOH", color: "#64748B" },
];

/* ══════════════════════════════════════════════
   TIME UTILITIES
   ══════════════════════════════════════════════ */

function generateTimes() {
  const t = [];
  for (let m = 0; m <= 1440; m += 30) {
    const h = Math.floor(m / 60) % 24, min = m % 60;
    const val = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    const ampm = h === 0 ? "12:00 AM" : h < 12 ? `${h}:${String(min).padStart(2, "0")} AM` : h === 12 ? `12:${String(min).padStart(2, "0")} PM` : `${h - 12}:${String(min).padStart(2, "0")} PM`;
    t.push({ value: val, label: ampm });
  }
  return t;
}
export const ALL_TIMES = generateTimes();

export function formatTime12(t24) {
  const m = ALL_TIMES.find(t => t.value === t24);
  return m ? m.label : t24;
}

/* ══════════════════════════════════════════════
   HOOKS
   ══════════════════════════════════════════════ */

export function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 640);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
}

/* ══════════════════════════════════════════════
   UTILITY FUNCTIONS
   ══════════════════════════════════════════════ */

export function getRoleColor(n) {
  const r = ROLE_PRESETS.find(p => p.name === n);
  return r ? r.color : "#999";
}

export function getInitials(n) {
  return n.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

export function formatPhone(raw) {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function matchRole(t) {
  const p = ROLE_PRESETS.find(r => r.name.toLowerCase() === t.trim().toLowerCase());
  return p ? p.name : "";
}

export function looksLikePhone(t) {
  return t.replace(/\D/g, "").length >= 7;
}

export function createEmployee(o = {}) {
  return { id: Date.now() + Math.random(), name: "", phone: "", role: "", availability: [...DAYS], ...o };
}

export function parseBulkText(text) {
  return text.split("\n").map(l => l.trim()).filter(l => l.length > 0).map(line => {
    const parts = line.split(/[,\t]+/).map(p => p.trim()).filter(p => p.length > 0);
    let name = "", role = "", phone = "";
    parts.forEach(part => {
      if (matchRole(part)) role = matchRole(part);
      else if (looksLikePhone(part)) phone = formatPhone(part);
      else if (!name) name = part;
    });
    return createEmployee({ name, role, phone });
  });
}

export function parseFileText(text) {
  const lines = text.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length === 0) return [];

  const firstLower = lines[0].toLowerCase();
  const isHeader = ["name", "employee", "first", "last", "role", "phone", "position", "title"].some(h => firstLower.includes(h));
  const dataLines = isHeader ? lines.slice(1) : lines;
  const delimiter = lines[0].includes("\t") ? "\t" : ",";

  let nameCol = 0, roleCol = -1, phoneCol = -1;
  if (isHeader) {
    const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase().replace(/['"]/g, ""));
    headers.forEach((h, i) => {
      if (["name", "employee", "employee name", "full name", "first name"].includes(h)) nameCol = i;
      if (["role", "position", "title", "job", "job title"].includes(h)) roleCol = i;
      if (["phone", "phone number", "mobile", "cell", "telephone"].includes(h)) phoneCol = i;
    });
    const firstIdx = headers.findIndex(h => h === "first name" || h === "first");
    const lastIdx = headers.findIndex(h => h === "last name" || h === "last");
    if (firstIdx >= 0 && lastIdx >= 0) {
      return dataLines.map(line => {
        const cols = line.split(delimiter).map(c => c.trim().replace(/^["']|["']$/g, ""));
        const name = `${cols[firstIdx] || ""} ${cols[lastIdx] || ""}`.trim();
        const role = roleCol >= 0 ? matchRole(cols[roleCol] || "") : "";
        const phone = phoneCol >= 0 && looksLikePhone(cols[phoneCol] || "") ? formatPhone(cols[phoneCol]) : "";
        return createEmployee({ name, role, phone });
      }).filter(e => e.name);
    }
  }

  return dataLines.map(line => {
    const cols = line.split(delimiter).map(c => c.trim().replace(/^["']|["']$/g, ""));
    if (isHeader) {
      const name = cols[nameCol] || "";
      const role = roleCol >= 0 ? matchRole(cols[roleCol] || "") : "";
      const phone = phoneCol >= 0 && looksLikePhone(cols[phoneCol] || "") ? formatPhone(cols[phoneCol]) : "";
      return createEmployee({ name, role, phone });
    }
    let name = "", role = "", phone = "";
    cols.forEach(part => {
      if (matchRole(part)) role = matchRole(part);
      else if (looksLikePhone(part)) phone = formatPhone(part);
      else if (!name) name = part;
    });
    return createEmployee({ name, role, phone });
  }).filter(e => e.name);
}

/* ══════════════════════════════════════════════
   ICONS
   ══════════════════════════════════════════════ */

export const Icons = {
  store: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  user: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  mapPin: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  phone: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c1.21.34 2 .57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  check: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  check14: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  checkCircle: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  users: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  chevDown: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  list: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  upload: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  file: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  x: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  zap: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  clock: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  grid: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  calendar: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  arrowRight: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
};

/* ══════════════════════════════════════════════
   GLOBAL CSS KEYFRAMES
   ══════════════════════════════════════════════ */

export const GLOBAL_STYLES = `
  @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes fadeSlide { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes slideLeft { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes slideRight { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes popIn { 0% { transform: scale(0); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
  @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes confetti1 { 0% { transform: translateY(0) rotate(0deg); opacity:1; } 100% { transform: translateY(-60px) translateX(30px) rotate(180deg); opacity:0; } }
  @keyframes confetti2 { 0% { transform: translateY(0) rotate(0deg); opacity:1; } 100% { transform: translateY(-50px) translateX(-25px) rotate(-150deg); opacity:0; } }
  @keyframes confetti3 { 0% { transform: translateY(0) rotate(0deg); opacity:1; } 100% { transform: translateY(-70px) translateX(15px) rotate(200deg); opacity:0; } }
  @keyframes modalIn { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  div::-webkit-scrollbar { display: none; }
`;

/* ══════════════════════════════════════════════
   SHARED UI COMPONENTS
   ══════════════════════════════════════════════ */

export function StepIndicator({ current, isMobile }) {
  if (isMobile) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 24 }}>
      {STEP_LABELS.map((l, i) => <div key={l} style={{ width: i === current ? 24 : 8, height: 8, borderRadius: 4, background: i < current ? "#2D8F5E" : i === current ? "#1a1a1a" : "#E0E0E0", transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)" }} />)}
      <span style={{ fontSize: 11, fontWeight: 500, color: "#999", marginLeft: 8, fontFamily: "'DM Sans',sans-serif" }}>Step {current + 1} of {STEP_LABELS.length}</span>
    </div>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 36 }}>
      {STEP_LABELS.map((l, i) => (
        <div key={l} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", background: i < current ? "#2D8F5E" : i === current ? "#1a1a1a" : "#E8E8E8", color: i <= current ? "#fff" : "#999", transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)", boxShadow: i === current ? "0 0 0 3px rgba(26,26,26,0.12)" : "none" }}>{i < current ? "\u2713" : i + 1}</div>
            <span style={{ fontSize: 11, fontWeight: i === current ? 600 : 400, color: i === current ? "#1a1a1a" : "#999", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.02em" }}>{l}</span>
          </div>
          {i < STEP_LABELS.length - 1 && <div style={{ width: 48, height: 2, background: i < current ? "#2D8F5E" : "#E8E8E8", margin: "0 8px", marginBottom: 20, borderRadius: 1 }} />}
        </div>
      ))}
    </div>
  );
}

export function NavButtons({ onBack, onNext, canNext, nextLabel = "Continue", isMobile, showBack = true, nextIcon }) {
  if (isMobile) return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#FFF", borderTop: "1px solid #EBEBEB", padding: "12px 16px", paddingBottom: "max(12px, env(safe-area-inset-bottom))", display: "flex", gap: 12, zIndex: 20 }}>
      {showBack && <button onClick={onBack} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1px solid #E5E5E5", background: "#FFF", cursor: "pointer", fontSize: 15, fontWeight: 500, color: "#666", fontFamily: "'DM Sans',sans-serif", WebkitTapHighlightColor: "transparent" }}>Back</button>}
      <button onClick={canNext ? onNext : undefined} style={{ flex: 2, padding: "14px", borderRadius: 12, border: "none", background: canNext ? "#1a1a1a" : "#E5E5E5", cursor: canNext ? "pointer" : "default", fontSize: 15, fontWeight: 600, color: canNext ? "#FFF" : "#999", fontFamily: "'DM Sans',sans-serif", boxShadow: canNext ? "0 2px 8px rgba(0,0,0,0.15)" : "none", WebkitTapHighlightColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {nextIcon && <span style={{ display: "flex" }}>{nextIcon}</span>}{nextLabel}
      </button>
    </div>
  );
  return (
    <div style={{ display: "flex", justifyContent: showBack ? "space-between" : "flex-end", alignItems: "center", marginTop: 24 }}>
      {showBack && <button onClick={onBack} style={{ padding: "12px 28px", borderRadius: 10, border: "1px solid #E5E5E5", background: "#FFF", cursor: "pointer", fontSize: 14, fontWeight: 500, color: "#666", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s ease" }} onMouseEnter={e => { e.target.style.borderColor = "#CCC"; e.target.style.background = "#FAFAFA"; }} onMouseLeave={e => { e.target.style.borderColor = "#E5E5E5"; e.target.style.background = "#FFF"; }}>{"\u2190"} Back</button>}
      <button onClick={canNext ? onNext : undefined} style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 40px", borderRadius: 12, border: "none", background: canNext ? "#1a1a1a" : "#E5E5E5", cursor: canNext ? "pointer" : "default", fontSize: 14, fontWeight: 600, color: canNext ? "#FFF" : "#999", fontFamily: "'DM Sans',sans-serif", boxShadow: canNext ? "0 4px 16px rgba(0,0,0,0.15)" : "none", transition: "all 0.2s ease" }} onMouseEnter={e => { if (canNext) { e.currentTarget.style.background = "#333"; e.currentTarget.style.transform = "translateY(-2px)"; } }} onMouseLeave={e => { if (canNext) { e.currentTarget.style.background = "#1a1a1a"; e.currentTarget.style.transform = "translateY(0)"; } }}>
        {nextIcon && <span style={{ display: "flex" }}>{nextIcon}</span>}{nextLabel}{!nextIcon && " \u2192"}
      </button>
    </div>
  );
}

export function Counter({ value, onChange, min = 0, max = 20, color, isMobile }) {
  const s = isMobile ? 44 : 36, n = isMobile ? 36 : 32, h = isMobile ? 44 : 38;
  const f = isMobile ? 20 : 18;
  return (
    <div style={{ display: "flex", alignItems: "center", borderRadius: 10, overflow: "hidden", border: "1px solid #E5E5E5", background: "#FAFAFA", height: h }}>
      <button onClick={e => { e.stopPropagation(); onChange(Math.max(min, value - 1)); }} style={{ width: s, height: "100%", border: "none", background: "transparent", cursor: value > min ? "pointer" : "default", fontSize: f, color: value > min ? "#666" : "#CCC", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif", WebkitTapHighlightColor: "transparent" }}>{"\u2212"}</button>
      <div style={{ width: n, textAlign: "center", fontSize: isMobile ? 16 : 15, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", color: value > 0 ? (color || "#1a1a1a") : "#CCC", borderLeft: "1px solid #E5E5E5", borderRight: "1px solid #E5E5E5", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: value > 0 ? `${color || "#1a1a1a"}08` : "transparent", transition: "all 0.2s ease" }}>{value}</div>
      <button onClick={e => { e.stopPropagation(); onChange(Math.min(max, value + 1)); }} style={{ width: s, height: "100%", border: "none", background: "transparent", cursor: value < max ? "pointer" : "default", fontSize: f, color: value < max ? "#666" : "#CCC", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif", WebkitTapHighlightColor: "transparent" }}>+</button>
    </div>
  );
}

export function DaySelector({ selectedDay, onSelect, isMobile, renderBadge }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current?.children[selectedDay]) ref.current.children[selectedDay].scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedDay]);
  return (
    <div ref={ref} style={{ display: "flex", gap: isMobile ? 8 : 6, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", padding: "4px 0 8px", marginBottom: 4, justifyContent: isMobile ? "flex-start" : "center" }}>
      {DAYS.map((day, di) => {
        const sel = di === selectedDay, we = di >= 5;
        const badge = renderBadge?.(di);
        return (
          <button key={day} onClick={() => onSelect(di)} style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: isMobile ? "10px 16px" : "10px 22px", borderRadius: 12, border: sel ? "2px solid #1a1a1a" : "2px solid #EBEBEB", background: sel ? "#1a1a1a" : "#FFF", cursor: "pointer", WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease", minWidth: isMobile ? 52 : 64, position: "relative" }}>
            <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", color: sel ? "#FFF" : we ? "#2D8F5E" : "#333", textTransform: "uppercase", letterSpacing: "0.06em" }}>{day}</span>
            {badge}
          </button>
        );
      })}
    </div>
  );
}

export function CopyMenu({ label, onCopy, show, setShow, isMobile, dayData, selectedDay }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (targets) => {
    onCopy(targets);
    setShow(false);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Build menu options filtered by open days
  const weekdayIndices = [0, 1, 2, 3, 4];
  const weekendIndices = [5, 6];
  const allIndices = [0, 1, 2, 3, 4, 5, 6];

  const filterOpen = (indices) => {
    if (!dayData) return indices.filter(i => i !== selectedDay);
    return indices.filter(i => i !== selectedDay && dayData[i]?.isOpen);
  };

  const openWeekdays = filterOpen(weekdayIndices);
  const openWeekends = filterOpen(weekendIndices);
  const openAll = filterOpen(allIndices);

  const options = [];
  if (openWeekdays.length > 0) {
    const allWdOpen = !dayData || weekdayIndices.filter(i => i !== selectedDay).every(i => dayData[i]?.isOpen);
    options.push({
      label: allWdOpen ? "Weekdays (Mon\u2013Fri)" : `Open weekdays (${openWeekdays.length})`,
      t: openWeekdays,
    });
  }
  if (openWeekends.length > 0) {
    const allWeOpen = !dayData || weekendIndices.filter(i => i !== selectedDay).every(i => dayData[i]?.isOpen);
    options.push({
      label: allWeOpen ? "Weekends (Sat\u2013Sun)" : `Open weekends (${openWeekends.length})`,
      t: openWeekends,
    });
  }
  if (openAll.length > 0 && openWeekdays.length > 0 && openWeekends.length > 0) {
    options.push({
      label: openAll.length === 6 ? "Every day" : `All open days (${openAll.length})`,
      t: openAll,
    });
  }

  // Don't render if no targets to copy to
  if (openAll.length === 0) return null;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setShow(!show)} style={{ display: "flex", alignItems: "center", gap: 5, padding: isMobile ? "8px 12px" : "8px 16px", borderRadius: 8, border: copied ? "1px solid #2D8F5E" : "1px solid #E5E5E5", background: copied ? "#2D8F5E08" : "#FFF", cursor: "pointer", fontSize: 13, fontWeight: 500, color: copied ? "#2D8F5E" : "#666", fontFamily: "'DM Sans',sans-serif", WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease" }}>
        {copied ? "\u2713 Copied!" : `\uD83D\uDCCB Copy ${label}\u2026`}
      </button>
      {show && <>
        <div onClick={() => setShow(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }} />
        <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: "#FFF", borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.15)", border: "1px solid #E5E5E5", padding: 4, zIndex: 10, minWidth: 210 }}>
          {options.map(({ label: l, t }) =>
            <button key={l} onClick={() => handleCopy(t)} style={{ display: "block", width: "100%", padding: isMobile ? "14px 16px" : "11px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 14, fontFamily: "'DM Sans',sans-serif", color: "#333", textAlign: "left", borderRadius: 8, WebkitTapHighlightColor: "transparent" }}>{l}</button>
          )}
        </div>
      </>}
    </div>
  );
}

export function TimeSelect({ value, onChange, isMobile, label }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 500, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "'DM Sans',sans-serif" }}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ width: "100%", padding: isMobile ? "12px" : "10px 12px", borderRadius: 10, border: "1px solid #E5E5E5", background: "#FAFAFA", fontSize: isMobile ? 15 : 14, fontWeight: 500, color: "#333", fontFamily: "'DM Sans',sans-serif", appearance: "none", WebkitAppearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", cursor: "pointer", outline: "none" }}>
        {ALL_TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
    </div>
  );
}
